/**
 * @fileoverview Bonded force kernels: harmonic bonds, harmonic angles, and
 * periodic (cosine-series) torsions.
 *
 * Each kernel accumulates the analytical Cartesian gradient into a
 * caller-provided flat buffer `grad` (length 3*N; the caller zeroes it) and
 * returns the scalar energy contribution (kJ/mol). No allocation happens on
 * the hot path — the angle/torsion routines write into module-level scratch
 * buffers (`SCRATCH9`, `SCRATCH12`) that are reused across every term.
 *
 * Conventions (engine contract):
 *   - `coords` and `grad` are flat `Float64Array`s of length `3*N`; atom `a`
 *     occupies `[3a, 3a+1, 3a+2]`. Index arrays hold *atom* indices.
 *   - Units: length nm, energy kJ/mol, angle rad; bond k in kJ/mol/nm^2,
 *     angle k in kJ/mol/rad^2, torsion k in kJ/mol.
 *   - No ½ prefactor on bonds/angles. The AMBER-convention force constants
 *     delivered on the v6 stream already bake the ½ in, so the energy is
 *         E_bond  = Σ k_b (r − r0)^2
 *         E_angle = Σ k_θ (θ − θ0)^2
 *     not the OpenMM ½·k form. Using ½ here would make energies 2× too small.
 *   - Torsions use the periodic form (also no ½):
 *         E_torsion = Σ_terms k_n [1 + cos(n·φ − γ)]
 *     following the OpenMM `ReferencePeriodicTorsionForce` structure: dE/dφ =
 *     −n·k_n·sin(n·φ − γ), chained through ∂φ/∂x from `dihedralGrad`.
 *
 * The geometry primitives (`angleGrad`, `dihedralGrad`) supply the angle value
 * and its per-atom Cartesian derivative; these kernels scale that derivative
 * by dE/d(angle) and scatter it into the global gradient.
 */

import { angleGrad, dihedralGrad } from '../geometry/angle.js';

// Module-level scratch reused by every angle/torsion term so the hot path
// never allocates. angleGrad writes 9 components ([a,b,c]); dihedralGrad
// writes 12 ([a,b,c,d]).
const SCRATCH9 = new Float64Array(9);
const SCRATCH12 = new Float64Array(12);

/**
 * Harmonic bond energy and gradient (no ½ prefactor):
 *     E = Σ_bonds k_b (r − r0)^2
 *     dE/dr = 2 k_b (r − r0)
 *     dE/dx_i = (dE/dr) (x_i − x_j) / r   (and its negative on atom j)
 *
 * @param {Float64Array} coords      Flat length-3N coordinate buffer (nm).
 * @param {Int32Array}   bondIndices Length 2*N_bonds, flat (i,j) atom pairs.
 * @param {Float64Array} bondK       Length N_bonds, k_b in kJ/mol/nm^2.
 * @param {Float64Array} bondR0      Length N_bonds, r0 in nm.
 * @param {Float64Array} grad        Flat length-3N gradient accumulator (caller-zeroed).
 * @returns {number}                 Bond energy contribution (kJ/mol).
 */
export function evalBonds(coords, bondIndices, bondK, bondR0, grad) {
    let energy = 0;
    const nBonds = bondK.length;

    for (let b = 0; b < nBonds; b++) {
        const ia = bondIndices[2 * b];
        const ib = bondIndices[2 * b + 1];
        const a3 = 3 * ia;
        const b3 = 3 * ib;

        const dx = coords[a3] - coords[b3];
        const dy = coords[a3 + 1] - coords[b3 + 1];
        const dz = coords[a3 + 2] - coords[b3 + 2];

        const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (r === 0) continue; // coincident atoms: undefined direction, no force.

        const k = bondK[b];
        const diff = r - bondR0[b];

        energy += k * diff * diff;

        // dE/dr = 2 k (r - r0); gOverR = (1/r) dE/dr.
        const gOverR = (2 * k * diff) / r;
        const gx = gOverR * dx;
        const gy = gOverR * dy;
        const gz = gOverR * dz;

        grad[a3] += gx;
        grad[a3 + 1] += gy;
        grad[a3 + 2] += gz;
        grad[b3] -= gx;
        grad[b3 + 1] -= gy;
        grad[b3 + 2] -= gz;
    }

    return energy;
}

/**
 * Harmonic angle energy and gradient (no ½ prefactor):
 *     E = Σ_angles k_θ (θ − θ0)^2
 *     dE/dθ = 2 k_θ (θ − θ0)
 *     dE/dx = (dE/dθ) ∂θ/∂x   (∂θ/∂x from angleGrad)
 *
 * @param {Float64Array} coords        Flat length-3N coordinate buffer (nm).
 * @param {Int32Array}   angleIndices  Length 3*N_angles, flat (i,j,k) triples (j = vertex).
 * @param {Float64Array} angleK        Length N_angles, k_θ in kJ/mol/rad^2.
 * @param {Float64Array} angleTheta0   Length N_angles, θ0 in rad.
 * @param {Float64Array} grad          Flat length-3N gradient accumulator (caller-zeroed).
 * @returns {number}                   Angle energy contribution (kJ/mol).
 */
export function evalAngles(coords, angleIndices, angleK, angleTheta0, grad) {
    let energy = 0;
    const nAngles = angleK.length;

    for (let a = 0; a < nAngles; a++) {
        const ia = angleIndices[3 * a];
        const ib = angleIndices[3 * a + 1]; // vertex
        const ic = angleIndices[3 * a + 2];

        const theta = angleGrad(coords, ia, ib, ic, SCRATCH9);

        const k = angleK[a];
        const diff = theta - angleTheta0[a];

        energy += k * diff * diff;

        // dE/dθ = 2 k (θ - θ0). Scatter dE/dθ * ∂θ/∂x.
        const dEdTheta = 2 * k * diff;
        const a3 = 3 * ia;
        const b3 = 3 * ib;
        const c3 = 3 * ic;

        grad[a3] += dEdTheta * SCRATCH9[0];
        grad[a3 + 1] += dEdTheta * SCRATCH9[1];
        grad[a3 + 2] += dEdTheta * SCRATCH9[2];
        grad[b3] += dEdTheta * SCRATCH9[3];
        grad[b3 + 1] += dEdTheta * SCRATCH9[4];
        grad[b3 + 2] += dEdTheta * SCRATCH9[5];
        grad[c3] += dEdTheta * SCRATCH9[6];
        grad[c3 + 1] += dEdTheta * SCRATCH9[7];
        grad[c3 + 2] += dEdTheta * SCRATCH9[8];
    }

    return energy;
}

/**
 * Periodic torsion energy and gradient (cosine series, no ½ prefactor):
 *     E = Σ_terms k_n [1 + cos(n·φ − γ)]
 *     dE/dφ = − Σ_terms n·k_n·sin(n·φ − γ)
 *     dE/dx = (dE/dφ) ∂φ/∂x   (∂φ/∂x from dihedralGrad)
 *
 * A single (i,j,k,l) quad may carry several cosine terms (different
 * periodicities/phases). `torsionTermsPerTorsion[t]` gives the term count for
 * torsion `t`; the per-term arrays (`torsionK`, `torsionN`, `torsionPhase`)
 * are flat and consumed in order. φ and ∂φ/∂x are computed once per quad and
 * shared across that quad's terms (dE/dφ accumulates over the terms before a
 * single scatter), matching the OpenMM `ReferencePeriodicTorsionForce`
 * structure.
 *
 * @param {Float64Array} coords                Flat length-3N coordinate buffer (nm).
 * @param {Int32Array}   torsionIndices        Length 4*N_torsions, flat (i,j,k,l) quads.
 * @param {Int32Array}   torsionTermsPerTorsion Length N_torsions, cosine-term count per quad.
 * @param {Float64Array} torsionK              Length sum(termsPerTorsion), k_n in kJ/mol.
 * @param {Int32Array}   torsionN              Length sum(termsPerTorsion), periodicity n (integer).
 * @param {Float64Array} torsionPhase          Length sum(termsPerTorsion), phase γ in rad.
 * @param {Float64Array} grad                  Flat length-3N gradient accumulator (caller-zeroed).
 * @returns {number}                           Torsion energy contribution (kJ/mol).
 */
export function evalTorsions(
    coords, torsionIndices, torsionTermsPerTorsion, torsionK, torsionN, torsionPhase, grad,
) {
    let energy = 0;
    const nTorsions = torsionTermsPerTorsion.length;
    let term = 0; // running index into the flat per-term arrays.

    for (let t = 0; t < nTorsions; t++) {
        const ia = torsionIndices[4 * t];
        const ib = torsionIndices[4 * t + 1];
        const ic = torsionIndices[4 * t + 2];
        const id = torsionIndices[4 * t + 3];

        const phi = dihedralGrad(coords, ia, ib, ic, id, SCRATCH12);

        const nTerms = torsionTermsPerTorsion[t];
        let dEdPhi = 0;

        for (let s = 0; s < nTerms; s++, term++) {
            const k = torsionK[term];
            const n = torsionN[term];
            const phase = torsionPhase[term];

            const arg = n * phi - phase;
            // E = k (1 + cos(arg)); dE/dφ = -n k sin(arg).
            energy += k * (1 + Math.cos(arg));
            dEdPhi -= n * k * Math.sin(arg);
        }

        // Scatter dE/dφ * ∂φ/∂x once for the whole quad.
        const a3 = 3 * ia;
        const b3 = 3 * ib;
        const c3 = 3 * ic;
        const d3 = 3 * id;

        grad[a3] += dEdPhi * SCRATCH12[0];
        grad[a3 + 1] += dEdPhi * SCRATCH12[1];
        grad[a3 + 2] += dEdPhi * SCRATCH12[2];
        grad[b3] += dEdPhi * SCRATCH12[3];
        grad[b3 + 1] += dEdPhi * SCRATCH12[4];
        grad[b3 + 2] += dEdPhi * SCRATCH12[5];
        grad[c3] += dEdPhi * SCRATCH12[6];
        grad[c3 + 1] += dEdPhi * SCRATCH12[7];
        grad[c3 + 2] += dEdPhi * SCRATCH12[8];
        grad[d3] += dEdPhi * SCRATCH12[9];
        grad[d3 + 1] += dEdPhi * SCRATCH12[10];
        grad[d3 + 2] += dEdPhi * SCRATCH12[11];
    }

    return energy;
}
